<template>
  <v-dialog
    v-model="showModal"
    max-width="690px"
    content-class="gpt-suggestion-modal-position"
    persistent
    no-click-animation
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        color="orange"
        class="white--text mr-2"
        depressed
        fab
        small
      >
        <v-icon>mdi-message-reply-text-outline</v-icon>
      </v-btn>
    </template>

    <v-card class="gpt-suggestion-modal">
      <v-card-title class="d-flex align-center justify-space-between py-2">
        <span class="text-subtitle-1 font-weight-medium">
          ИИ подсказка {{ suggestionIndex + 1 }}/{{ suggestions.length }}
        </span>
        <div class="d-flex align-center">
          <v-btn icon small @click="prev" :disabled="suggestions.length <= 1">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon small @click="next" :disabled="suggestions.length <= 1">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn icon small @click="close" class="ml-1">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="py-3" style="max-height: 400px; overflow-y: auto">
        <!-- Message -->
        <div v-if="currentSuggestion.message" class="mb-3">
          <div class="text-caption grey--text mb-1">Сообщение</div>
          <div style="white-space: pre-wrap" class="body-2">
            {{ currentSuggestion.message }}
          </div>
        </div>

        <!-- Multimedia preview -->
        <div
          v-if="
            currentSuggestion.multimedia &&
            currentSuggestion.multimedia.length > 0
          "
          class="mb-3"
        >
          <div class="text-caption grey--text mb-1">Медиа</div>
          <div class="d-flex flex-wrap" style="gap: 8px">
            <div
              v-for="(url, idx) in currentSuggestion.multimedia"
              :key="idx"
              class="media-preview"
            >
              <img
                v-if="isImage(url)"
                :src="url"
                class="media-preview__image"
              />
              <video
                v-else-if="isVideo(url)"
                :src="url"
                class="media-preview__image"
                muted
              />
              <div v-else class="media-preview__doc d-flex align-center pa-2">
                <v-icon small class="mr-1">mdi-file-outline</v-icon>
                <span class="text-truncate" style="max-width: 80px">
                  {{ getFileName(url) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Category -->
        <div v-if="currentSuggestion.category" class="mb-3">
          <div class="text-caption grey--text mb-1">Категория</div>
          <div class="d-flex align-center">
            <v-chip
              :color="getCategoryColor(currentSuggestion.category)"
              dark
              class="mr-2"
            >
              {{ getCategoryName(currentSuggestion.category) }}
            </v-chip>
            <v-btn
              small
              depressed
              color="blue"
              class="white--text"
              :disabled="categoryDone"
              :loading="categoryLoading"
              @click="changeCategory"
            >
              {{ categoryButtonText }}
            </v-btn>
          </div>
        </div>

        <!-- Comment -->
        <div v-if="currentSuggestion.comment" class="mb-1">
          <div class="text-caption grey--text mb-1">Комментарий</div>
          <div class="d-flex align-center">
            <span class="body-2 mr-2" style="white-space: pre-wrap">{{
              currentSuggestion.comment
            }}</span>
            <v-btn
              small
              depressed
              color="blue"
              class="white--text"
              :disabled="commentDone"
              :loading="commentLoading"
              @click="addComment"
            >
              {{ commentDone ? 'Добавлен' : 'Добавить' }}
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-4 py-2">
        <v-btn
          small
          text
          color="green"
          :disabled="!hasAnyAction"
          :loading="allLoading"
          @click="doAll"
        >
          <v-icon small class="mr-1">mdi-check-all</v-icon>
          Выполнить все
        </v-btn>
        <v-spacer />
        <v-btn
          small
          text
          color="blue"
          :disabled="!currentSuggestion.message && !hasMultimedia"
          @click="selectSuggestion"
        >
          Выбрать
        </v-btn>
        <v-btn
          small
          text
          color="blue darken-2"
          :disabled="!currentSuggestion.message && !hasMultimedia"
          :loading="sendLoading"
          @click="sendSuggestion"
        >
          Отправить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import messagesApi from '@/api/messages';
import { SUPPORT_DECISION } from '@/lib/const';
import { DEPARTMENTS } from '@/models/departments';
import {
  isImageUrl,
  isVideoUrl,
  getFileNameFromUrl,
  downloadUrlsToUploadFiles,
} from '@/lib/fileUtils';

export default {
  name: 'GptSuggestionModal',

  props: {
    suggestions: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      showModal: false,
      suggestionIndex: 0,
      categoryDone: false,
      categoryLoading: false,
      commentDone: false,
      commentLoading: false,
      sendLoading: false,
      allLoading: false,
    };
  },

  computed: {
    currentSuggestion() {
      return this.suggestions[this.suggestionIndex] || {};
    },

    currentChatId() {
      return this.$store.state.chat.currentChatId;
    },

    currentChat() {
      return this.$store.getters['chat/currentChat'];
    },

    gptData() {
      return this.$store.state.gpt.data;
    },

    lastChatMessageId() {
      const lastMessage = this.$store.getters['chat/lastChatMessage'](
        this.currentChatId
      );
      return lastMessage?.messageId || null;
    },

    hasMultimedia() {
      return (
        this.currentSuggestion.multimedia &&
        this.currentSuggestion.multimedia.length > 0
      );
    },

    hasAnyAction() {
      const s = this.currentSuggestion;
      const hasMessage = !!s.message || this.hasMultimedia;
      const hasCategory = !!s.category && !this.categoryDone;
      const hasComment = !!s.comment && !this.commentDone;
      return hasMessage || hasCategory || hasComment;
    },

    categoryButtonText() {
      if (this.categoryDone) return 'Готово';
      const hasDept = this.currentChat?.departments?.includes(
        this.currentSuggestion.category
      );
      return hasDept ? 'Убрать' : 'Добавить';
    },
  },

  watch: {
    suggestionIndex() {
      this.resetActionStates();
    },
  },

  methods: {
    next() {
      this.suggestionIndex =
        this.suggestionIndex + 1 >= this.suggestions.length
          ? 0
          : this.suggestionIndex + 1;
    },

    prev() {
      this.suggestionIndex =
        this.suggestionIndex === 0
          ? this.suggestions.length - 1
          : this.suggestionIndex - 1;
    },

    close() {
      this.showModal = false;
    },

    resetActionStates() {
      this.categoryDone = false;
      this.commentDone = false;
    },

    isImage: isImageUrl,

    isVideo: isVideoUrl,

    getFileName: getFileNameFromUrl,

    getCategoryName(categoryId) {
      const dept = DEPARTMENTS.find(d => d.id === categoryId);
      return dept ? dept.name : categoryId;
    },

    getCategoryColor(categoryId) {
      const dept = DEPARTMENTS.find(d => d.id === categoryId);
      return dept ? dept.color : 'grey';
    },

    getGptDataToPersist() {
      this.$store.commit('gpt/UPDATE_DATA', {
        key: 'supportDecision',
        value: SUPPORT_DECISION.YES,
      });
      return this.$store.state.gpt.data;
    },

    async changeCategory() {
      if (!this.currentSuggestion.category || !this.currentChat) return;
      this.categoryLoading = true;
      try {
        const departments = [...(this.currentChat.departments || [])];
        const idx = departments.indexOf(this.currentSuggestion.category);
        if (idx >= 0) {
          departments.splice(idx, 1);
        } else {
          departments.push(this.currentSuggestion.category);
        }
        await this.$store.dispatch(
          'chat/set_departments',
          {
            chatId: this.currentChatId,
            departments,
            gptData: this.getGptDataToPersist(),
          },
          { root: true }
        );
        this.categoryDone = true;
      } catch (e) {
        console.error(e);
      } finally {
        this.categoryLoading = false;
      }
    },

    async addComment() {
      if (!this.currentSuggestion.comment) return;
      const targetMessageId = this.lastChatMessageId;
      if (!targetMessageId) return;
      this.commentLoading = true;
      try {
        await this.$store.dispatch(
          'chat/set_message_comment',
          {
            messageId: targetMessageId,
            chatId: this.currentChatId,
            comment: this.currentSuggestion.comment,
          },
          { root: true }
        );
        this.commentDone = true;
      } catch (e) {
        console.error(e);
      } finally {
        this.commentLoading = false;
      }
    },

    async downloadMultimedia() {
      const urls = this.currentSuggestion.multimedia || [];
      return downloadUrlsToUploadFiles(urls);
    },

    async sendMedia(gptData) {
      const files = await this.downloadMultimedia();
      for (const fileItem of files) {
        if (fileItem.thumbnail) {
          await messagesApi.sendPhoto(
            this.currentChatId,
            fileItem.file,
            '',
            null,
            gptData
          );
        } else {
          await messagesApi.sendDocument(
            this.currentChatId,
            fileItem.file,
            '',
            null,
            gptData
          );
        }
      }
    },

    selectSuggestion() {
      this.$store.commit('gpt/UPDATE_DATA', {
        key: 'supportDecision',
        value: SUPPORT_DECISION.YES,
      });

      const message = this.currentSuggestion.message || '';
      this.$store.commit('chat/SET_MESSAGE_INPUT', message, { root: true });

      if (this.hasMultimedia) {
        this.showModal = false;
        this.openUploadDialogWithMedia();
      } else {
        this.showModal = false;
      }

      this.$emit('select', this.suggestionIndex);
    },

    async openUploadDialogWithMedia() {
      const files = await this.downloadMultimedia();
      if (files.length === 0) return;

      this.$store.commit(
        'ui/SET_CURRENT_DIALOG',
        { name: 'uploadDialog', data: files },
        { root: true }
      );
    },

    async sendSuggestion() {
      this.sendLoading = true;
      try {
        await this.sendMessageAndMedia();
        this.showModal = false;
        this.$emit('send', this.suggestionIndex);
      } catch (e) {
        console.error(e);
      } finally {
        this.sendLoading = false;
      }
    },

    async sendMessageAndMedia() {
      const message = this.currentSuggestion.message || '';
      const gptData = this.getGptDataToPersist();

      // 1. Send media first
      if (this.hasMultimedia) {
        await this.sendMedia(gptData);
      }

      // 2. Send text message
      if (message) {
        await messagesApi.create(this.currentChatId, message, null, gptData);
      }

      this.clearGptState();
    },

    clearGptState() {
      this.$store.commit('chat/SET_MESSAGE_INPUT', '', { root: true });
      this.$store.commit('gpt/SET_MODE', null);
      this.$store.commit('gpt/SET_DATA', null);
      this.$store.commit('ui/SET_SCROLL_TO_BOTTOM', true, { root: true });
    },

    async doAll() {
      this.allLoading = true;
      try {
        const gptData = this.getGptDataToPersist();

        // 1. Send media if present
        if (this.hasMultimedia) {
          await this.sendMedia(gptData);
        }

        // 2. Send text message if present
        if (this.currentSuggestion.message) {
          await messagesApi.create(
            this.currentChatId,
            this.currentSuggestion.message,
            null,
            gptData
          );
        }

        // 3. Change category if present and not done
        if (this.currentSuggestion.category && !this.categoryDone) {
          const departments = [...(this.currentChat.departments || [])];
          const idx = departments.indexOf(this.currentSuggestion.category);
          if (idx >= 0) {
            departments.splice(idx, 1);
          } else {
            departments.push(this.currentSuggestion.category);
          }
          await this.$store.dispatch(
            'chat/set_departments',
            { chatId: this.currentChatId, departments, gptData },
            { root: true }
          );
          this.categoryDone = true;
        }

        // 4. Add comment to last message in chat if present and not done
        if (this.currentSuggestion.comment && !this.commentDone) {
          const targetMessageId = this.lastChatMessageId;
          if (targetMessageId) {
            await this.$store.dispatch(
              'chat/set_message_comment',
              {
                messageId: targetMessageId,
                chatId: this.currentChatId,
                comment: this.currentSuggestion.comment,
              },
              { root: true }
            );
            this.commentDone = true;
          }
        }

        this.clearGptState();
        this.showModal = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.allLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.gpt-suggestion-modal-position {
  align-self: flex-start;
  margin-top: 48px !important;
}

.gpt-suggestion-modal {
  .media-preview {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.12);

    &__image {
      width: 100px;
      height: 100px;
      object-fit: cover;
      display: block;
    }

    &__doc {
      width: 100px;
      height: 60px;
      background: #f5f5f5;
      font-size: 11px;
    }
  }
}
</style>
